import type React from 'react';
import { useState, useCallback } from 'react';
import {
  Checkbox,
  Input,
  Text,
  Badge,
  Dropdown,
  Option,
  Tooltip,
  Switch,
} from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular, InfoRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'hooks';
import { IncidentSeverity, type NotificationRule, type GroupConfig, type NotificationCapabilities } from 'types';
import { SeverityBadge } from './SeverityBadge';
import { useNotificationRuleMatrixStyles } from './NotificationRuleMatrix.styles';

interface NotificationRuleMatrixProps {
  rules: NotificationRule[];
  groups: GroupConfig[];
  onRuleChange: (rule: NotificationRule) => void;
  customerId?: string | null;
  capabilities?: NotificationCapabilities;
}

const SEVERITY_ORDER = [
  IncidentSeverity.High,
  IncidentSeverity.Medium,
  IncidentSeverity.Low,
  IncidentSeverity.Informational,
];

export const NotificationRuleMatrix: React.FC<NotificationRuleMatrixProps> = ({
  rules,
  groups,
  onRuleChange,
  customerId,
  capabilities,
}) => {
  const styles = useNotificationRuleMatrixStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [expandedSeverity, setExpandedSeverity] = useState<IncidentSeverity | null>(null);

  // Default capabilities to false - channels are disabled until API confirms they're configured
  const caps = capabilities ?? { emailEnabled: false, teamsEnabled: false, teamsDirectMessageEnabled: false };

  const renderInfoIcon = (content: string) => (
    <Tooltip content={content} relationship="description">
      <span className={styles.infoIconWrap} onClick={(event) => event.stopPropagation()}>
        <InfoRegular className={styles.infoIcon} />
      </span>
    </Tooltip>
  );

  const getMobileSummaryValue = useCallback((isEnabled: boolean) => {
    return isEnabled
      ? t('statusPanel.settings.notificationsMatrix.summaryEnabled')
      : t('statusPanel.settings.notificationsMatrix.summaryDisabled');
  }, [t]);

  // SLA threshold notifications are currently tied to Teams channel availability.
  // This can be extended later to support additional channels (for example email).
  const canConfigureSlaThresholdNotifications = (rule: NotificationRule): boolean => {
    return rule.channels.teams;
  };

  const findStoredRule = (severity: IncidentSeverity): NotificationRule => {
    const found =
      rules.find((r) => r.severity === severity && r.customerId === customerId) ||
      rules.find((r) => r.severity === severity && r.customerId === null);
    if (found) return found;
    return {
      severity,
      channels: { statusPanel: true, email: false, teams: false, audio: false },
      customerId: customerId || null,
      notifyOnAckSlaExceeded: true,
      notifyOnUpdateSlaExceeded: true,
      notifyOnSlaBreached: true,
      notificationGroupIds: [],
      notificationEmails: [],
      notifyAdminGroupForUnowned: true,
    };
  };

  const getRule = (severity: IncidentSeverity): NotificationRule => {
    const rule = findStoredRule(severity);
    // Hide channel selections for capabilities the backend reports as unconfigured.
    return {
      ...rule,
      channels: {
        ...rule.channels,
        email: caps.emailEnabled ? rule.channels.email : false,
        teams: (caps.teamsEnabled || caps.teamsDirectMessageEnabled) ? rule.channels.teams : false,
      },
    };
  };

  const updateRule = useCallback(
    (severity: IncidentSeverity, updates: Partial<NotificationRule>) => {
      onRuleChange({ ...findStoredRule(severity), ...updates });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRuleChange, rules, customerId]
  );

  return (
    <div className={styles.container}>
      <Text size={200} style={{ color: 'var(--colorNeutralForeground3)' }}>
        {t('statusPanel.settings.notificationRulesDescription')}
      </Text>

      <div className={styles.matrix}>
        {/* Header */}
        {!isMobile && (
          <div className={`${styles.row} ${styles.headerRow}`}>
            <span>{t('statusPanel.settings.severity')}</span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.panel')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationPanel'))}
            </span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.email')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationEmail'))}
            </span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.teams')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationTeams'))}
            </span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.audio')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationAudio'))}
            </span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.ackSla')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationAckSla'))}
            </span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.updateSla')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationUpdateSla'))}
            </span>
            <span className={styles.headerCell}>
              <span>{t('statusPanel.settings.notificationsMatrix.groups')}</span>
              {renderInfoIcon(t('statusPanel.settings.tooltips.notificationRules'))}
            </span>
          </div>
        )}

        {/* Rows per severity */}
        {SEVERITY_ORDER.map((severity) => {
          const rule = getRule(severity);
          const isExpanded = expandedSeverity === severity;

          if (isMobile) {
            return (
              <div key={severity} className={styles.mobileCard}>
                <div
                  className={styles.mobileCardHeader}
                  onClick={() => setExpandedSeverity(isExpanded ? null : severity)}
                >
                  <SeverityBadge severity={severity} size="medium" />
                  <div className={styles.mobileCardSummary}>
                    <Text size={200}>
                      {t('statusPanel.settings.notificationsMatrix.email')}: {getMobileSummaryValue(rule.channels.email)}
                    </Text>
                    <Text size={200}>
                      {t('statusPanel.settings.notificationsMatrix.teams')}: {getMobileSummaryValue(rule.channels.teams)}
                    </Text>
                    <Text size={200}>
                      {t('statusPanel.settings.notificationsMatrix.audio')}: {getMobileSummaryValue(rule.channels.audio)}
                    </Text>
                  </div>
                  <div className={styles.mobileCardChevronWrap}>
                    {rule.notificationGroupIds.length > 0 && (
                      <Badge appearance="filled" color="informative">
                        {rule.notificationGroupIds.length}
                      </Badge>
                    )}
                    {isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                  </div>
                </div>

                {isExpanded && (
                  <div className={styles.mobileExpandedCard}>
                    <div className={styles.mobileSwitchGrid}>
                      <div className={styles.mobileSwitchRow}>
                        <Text size={200}>{t('statusPanel.settings.notificationsMatrix.panel')}</Text>
                        <Switch checked disabled label="" />
                      </div>
                      <div className={styles.mobileSwitchRow}>
                        <Text size={200}>{t('statusPanel.settings.notificationsMatrix.email')}</Text>
                        <Switch
                          checked={rule.channels.email}
                          disabled={!caps.emailEnabled}
                          onChange={(_, data) =>
                            updateRule(severity, {
                              channels: { ...rule.channels, email: Boolean(data.checked) },
                            })
                          }
                        />
                      </div>
                      <div className={styles.mobileSwitchRow}>
                        <Text size={200}>{t('statusPanel.settings.notificationsMatrix.teams')}</Text>
                        <Switch
                          checked={rule.channels.teams}
                          disabled={!caps.teamsEnabled && !caps.teamsDirectMessageEnabled}
                          onChange={(_, data) => {
                            const teamsEnabled = Boolean(data.checked);
                            updateRule(severity, {
                              channels: { ...rule.channels, teams: teamsEnabled },
                              notifyOnAckSlaExceeded: teamsEnabled ? rule.notifyOnAckSlaExceeded : false,
                              notifyOnUpdateSlaExceeded: teamsEnabled ? rule.notifyOnUpdateSlaExceeded : false,
                            });
                          }}
                        />
                      </div>
                      <div className={styles.mobileSwitchRow}>
                        <Text size={200}>{t('statusPanel.settings.notificationsMatrix.audio')}</Text>
                        <Switch
                          checked={rule.channels.audio}
                          onChange={(_, data) =>
                            updateRule(severity, {
                              channels: { ...rule.channels, audio: Boolean(data.checked) },
                            })
                          }
                        />
                      </div>
                      <div className={styles.mobileSwitchRow}>
                        <Text size={200}>{t('statusPanel.settings.notificationsMatrix.ackSla')}</Text>
                        <Switch
                          checked={rule.notifyOnAckSlaExceeded}
                          disabled={!canConfigureSlaThresholdNotifications(rule)}
                          onChange={(_, data) =>
                            updateRule(severity, {
                              notifyOnAckSlaExceeded: Boolean(data.checked),
                            })
                          }
                        />
                      </div>
                      <div className={styles.mobileSwitchRow}>
                        <Text size={200}>{t('statusPanel.settings.notificationsMatrix.updateSla')}</Text>
                        <Switch
                          checked={rule.notifyOnUpdateSlaExceeded}
                          disabled={!canConfigureSlaThresholdNotifications(rule)}
                          onChange={(_, data) =>
                            updateRule(severity, {
                              notifyOnUpdateSlaExceeded: Boolean(data.checked),
                            })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <div className={styles.fieldHeaderWithInfo}>
                        <Text weight="semibold" size={200} style={{ marginBottom: '8px', display: 'block' }}>
                          {t('statusPanel.settings.notificationGroupsLabel')}
                        </Text>
                        {renderInfoIcon(t('statusPanel.settings.tooltips.notificationGroups'))}
                      </div>
                      <Dropdown
                        multiselect
                        placeholder={t('statusPanel.settings.notificationGroupsPlaceholder')}
                        selectedOptions={rule.notificationGroupIds}
                        defaultSelectedOptions={rule.notificationGroupIds}
                        defaultValue={rule.notificationGroupIds.length > 0
                          ? groups.filter(g => rule.notificationGroupIds.includes(g.groupId)).map(g => g.displayName).join(', ')
                          : undefined
                        }
                        onOptionSelect={(_, data) =>
                          updateRule(severity, { notificationGroupIds: data.selectedOptions })
                        }
                        style={{ width: '100%' }}
                      >
                        {groups.map((group) => {
                          const label = group.cachedMemberCount > 0
                            ? `${group.displayName} (${group.cachedMemberCount})`
                            : group.displayName;
                          return (
                            <Option key={group.groupId} value={group.groupId} text={label}>
                              {label}
                            </Option>
                          );
                        })}
                      </Dropdown>
                    </div>

                    <div>
                      <div className={styles.fieldHeaderWithInfo}>
                        <Text weight="semibold" size={200} style={{ marginBottom: '8px', display: 'block' }}>
                          {t('statusPanel.settings.additionalRecipientsLabel')}
                        </Text>
                        {renderInfoIcon(t('statusPanel.settings.tooltips.additionalRecipients'))}
                      </div>
                      <Input
                        placeholder={t('statusPanel.settings.additionalRecipientsPlaceholder')}
                        value={rule.notificationEmails.join(', ')}
                        onChange={(_, data) => {
                          const emails = data.value
                            .split(',')
                            .map(e => e.trim())
                            .filter(e => e.length > 0);
                          updateRule(severity, { notificationEmails: emails });
                        }}
                        style={{ width: '100%' }}
                      />
                      <Text size={100} style={{ color: 'var(--colorNeutralForeground3)', marginTop: '4px', display: 'block' }}>
                        {t('statusPanel.settings.additionalRecipientsHelp')}
                      </Text>
                    </div>

                    <div className={styles.fieldWithInfo}>
                      <Checkbox
                        label={t('statusPanel.settings.notifyAdminForUnowned')}
                        checked={rule.notifyAdminGroupForUnowned}
                        onChange={(_, data) =>
                          updateRule(severity, { notifyAdminGroupForUnowned: Boolean(data.checked) })
                        }
                      />
                      {renderInfoIcon(t('statusPanel.settings.tooltips.notifyAdminForUnowned'))}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div key={severity}>
              <div
                className={styles.row}
                onClick={() => setExpandedSeverity(isExpanded ? null : severity)}
                style={{ cursor: 'pointer' }}
              >
                <SeverityBadge severity={severity} size="medium" />
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={true}
                    disabled={true}
                    title={t('statusPanel.settings.tooltips.statusPanelAlwaysOn')}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={rule.channels.email}
                    disabled={!caps.emailEnabled}
                    onChange={(_, data) =>
                      updateRule(severity, {
                        channels: { ...rule.channels, email: Boolean(data.checked) },
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={rule.channels.teams}
                    disabled={!caps.teamsEnabled && !caps.teamsDirectMessageEnabled}
                    onChange={(_, data) => {
                      const teamsEnabled = Boolean(data.checked);
                      updateRule(severity, {
                        channels: { ...rule.channels, teams: teamsEnabled },
                        notifyOnAckSlaExceeded: teamsEnabled ? rule.notifyOnAckSlaExceeded : false,
                        notifyOnUpdateSlaExceeded: teamsEnabled ? rule.notifyOnUpdateSlaExceeded : false,
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={rule.channels.audio}
                    onChange={(_, data) =>
                      updateRule(severity, {
                        channels: { ...rule.channels, audio: Boolean(data.checked) },
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={rule.notifyOnAckSlaExceeded}
                    disabled={!canConfigureSlaThresholdNotifications(rule)}
                    onChange={(_, data) =>
                      updateRule(severity, {
                        notifyOnAckSlaExceeded: Boolean(data.checked),
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.checkbox}>
                  <Checkbox
                    checked={rule.notifyOnUpdateSlaExceeded}
                    disabled={!canConfigureSlaThresholdNotifications(rule)}
                    onChange={(_, data) =>
                      updateRule(severity, {
                        notifyOnUpdateSlaExceeded: Boolean(data.checked),
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {isExpanded ? (
                    <ChevronDownRegular style={{ fontSize: '16px' }} />
                  ) : (
                    <ChevronRightRegular style={{ fontSize: '16px' }} />
                  )}
                  {rule.notificationGroupIds.length > 0 && (
                    <Badge appearance="filled" color="informative">
                      {rule.notificationGroupIds.length}
                    </Badge>
                  )}
                  <span style={{ fontSize: '12px' }}>
                    {rule.notificationGroupIds.length === 0
                      ? t('statusPanel.settings.notificationsMatrix.expand')
                      : t('statusPanel.settings.notificationsMatrix.groups')}
                  </span>
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--colorNeutralBackground2)',
                    borderRadius: '4px',
                    marginLeft: '8px',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                    }}
                  >
                    {/* Notify admin for unowned */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className={styles.fieldWithInfo}>
                        <Checkbox
                          label={t('statusPanel.settings.notifyAdminForUnowned')}
                          checked={rule.notifyAdminGroupForUnowned}
                          onChange={(_, data) =>
                            updateRule(severity, { notifyAdminGroupForUnowned: Boolean(data.checked) })
                          }
                        />
                        {renderInfoIcon(t('statusPanel.settings.tooltips.notifyAdminForUnowned'))}
                      </div>
                    </div>

                    {/* Group selector */}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className={styles.fieldHeaderWithInfo}>
                        <Text weight="semibold" size={200} style={{ marginBottom: '8px', display: 'block' }}>
                          {t('statusPanel.settings.notificationGroupsLabel')}
                        </Text>
                        {renderInfoIcon(t('statusPanel.settings.tooltips.notificationGroups'))}
                      </div>
                      <Dropdown
                        multiselect
                        placeholder={t('statusPanel.settings.notificationGroupsPlaceholder')}
                        selectedOptions={rule.notificationGroupIds}
                        defaultSelectedOptions={rule.notificationGroupIds}
                        defaultValue={rule.notificationGroupIds.length > 0
                          ? groups.filter(g => rule.notificationGroupIds.includes(g.groupId)).map(g => g.displayName).join(', ')
                          : undefined
                        }
                        onOptionSelect={(_, data) =>
                          updateRule(severity, { notificationGroupIds: data.selectedOptions })
                        }
                        onClick={(e) => e.stopPropagation()}
                        style={{ minWidth: '300px' }}
                      >
                        {groups.map((group) => {
                          const label = group.cachedMemberCount > 0
                            ? `${group.displayName} (${group.cachedMemberCount})`
                            : group.displayName;
                          return (
                            <Option key={group.groupId} value={group.groupId} text={label}>
                              {label}
                            </Option>
                          );
                        })}
                      </Dropdown>
                    </div>

                    {/* Individual email/UPN addresses */}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className={styles.fieldHeaderWithInfo}>
                        <Text weight="semibold" size={200} style={{ marginBottom: '8px', display: 'block' }}>
                          {t('statusPanel.settings.additionalRecipientsLabel')}
                        </Text>
                        {renderInfoIcon(t('statusPanel.settings.tooltips.additionalRecipients'))}
                      </div>
                      <Input
                        placeholder={t('statusPanel.settings.additionalRecipientsPlaceholder')}
                        value={rule.notificationEmails.join(', ')}
                        onChange={(_, data) => {
                          const emails = data.value
                            .split(',')
                            .map(e => e.trim())
                            .filter(e => e.length > 0);
                          updateRule(severity, { notificationEmails: emails });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                      />
                      <Text size={100} style={{ color: 'var(--colorNeutralForeground3)', marginTop: '4px', display: 'block' }}>
                        {t('statusPanel.settings.additionalRecipientsHelp')}
                      </Text>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
